import { useEffect, useState } from 'react'
import { NOTES_NOTIFY_DIALOG_BUTTON_CLASS } from '../../constants/dialogs'
import { showValidationMessage } from '@/lib/utils/notifications'
import type { PublicNotesNotifySelection } from '../../utils/publicNotesSubmit'
import { isPublicNotesNotifySelectionValid } from '../../utils/publicNotesSubmit'
import { ResidentDialogShell } from './ResidentDialogShell'
import type { PublicNotesNotifyDialogProps } from '../../types'

/**
 * Angular `StatementPopupComponent` with `emailtype === 'notemessage'` and `type === 'external'`.
 */
export function PublicNotesNotifyDialog({
  isOpen,
  isLibertyRole,
  processing = false,
  onClose,
  onSubmit,
}: PublicNotesNotifyDialogProps) {
  const [emailSendTo, setEmailSendTo] = useState('')
  const [emailSendToProperty, setEmailSendToProperty] = useState('')

  useEffect(() => {
    if (!isOpen) return
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reset the form selections each time the dialog opens
    setEmailSendTo('')
    setEmailSendToProperty('')
  }, [isOpen])

  const handleSubmit = () => {
    const selection: PublicNotesNotifySelection = {
      email_sendto: emailSendTo,
      email_sendto_property: emailSendToProperty,
    }
    if (!isPublicNotesNotifySelectionValid(selection)) {
      showValidationMessage('Please select an option!')
      return
    }
    onSubmit(selection)
  }

  const isPropertyChecked = emailSendToProperty === 'property'
  const isReimbursementsChecked = emailSendTo === 'default_manager'
  const isSaveOnlyChecked = emailSendTo === 'none'

  const toggleProperty = () => {
    setEmailSendToProperty(isPropertyChecked ? '' : 'property')
    if (emailSendTo === 'none') setEmailSendTo('')
  }

  const toggleReimbursements = () => {
    setEmailSendTo(isReimbursementsChecked ? '' : 'default_manager')
  }

  const selectSaveOnly = () => {
    setEmailSendTo('none')
    setEmailSendToProperty('')
  }

  return (
    <ResidentDialogShell
      isOpen={isOpen}
      title="Notes & Messages"
      headerVariant="centeredTitle"
      onClose={onClose}
      panelDataTestId="public-notes-notify-dialog-panel"
      footer={
        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:flex-wrap sm:justify-center">
          <button
            type="button"
            disabled={processing}
            onClick={handleSubmit}
            data-testid="public-notes-notify-dialog-submit-button"
            className={NOTES_NOTIFY_DIALOG_BUTTON_CLASS}
          >
            {processing ? 'Please wait…' : 'Submit'}
          </button>
        </div>
      }
    >
      <div className="space-y-4 text-sm text-slate-900" data-testid="public-notes-notify-dialog-body">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
              Who should be notified?
            </p>
            <p className="mt-0.5 text-xs text-slate-500">
              Select one or both teams, or save without notifying.
            </p>
          </div>
          <span className="inline-flex shrink-0 items-center rounded-full bg-sky-50 px-2 py-0.5 text-xs font-semibold text-[#1597ff] ring-1 ring-inset ring-blue-500/20">
            Publicly Visible
          </span>
        </div>

        <div className="space-y-2">
          <div className="flex items-baseline gap-2">
            <span className="text-sm font-bold text-slate-900">Notify teams</span>
            <span className="text-xs text-slate-500">Select one or both</span>
          </div>
          <div className={`grid gap-3 ${isLibertyRole ? 'sm:grid-cols-2' : 'grid-cols-1'}`}>
            {isLibertyRole ? (
              <label
                className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3.5 text-sm font-medium transition-all ${
                  isPropertyChecked
                    ? 'border-[#1597ff] bg-sky-50/60 ring-1 ring-[#1597ff]/20 text-slate-900 shadow-2xs'
                    : 'border-slate-200/90 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50/50'
                }`}
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 shrink-0 rounded border-slate-300 text-[#1597ff] focus:ring-blue-100 cursor-pointer"
                  checked={isPropertyChecked}
                  onChange={toggleProperty}
                  data-testid="public-notes-notify-property-radio"
                />
                <span className="min-w-0 flex-1 leading-snug">Property</span>
              </label>
            ) : null}

            <label
              className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3.5 text-sm font-medium transition-all ${
                isReimbursementsChecked
                  ? 'border-[#1597ff] bg-sky-50/60 ring-1 ring-[#1597ff]/20 text-slate-900 shadow-2xs'
                  : 'border-slate-200/90 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50/50'
              }`}
            >
              <input
                type="checkbox"
                className="h-4 w-4 shrink-0 rounded border-slate-300 text-[#1597ff] focus:ring-blue-100 cursor-pointer"
                checked={isReimbursementsChecked}
                onChange={toggleReimbursements}
                data-testid="public-notes-notify-reimbursements-radio"
              />
              <span className="min-w-0 flex-1 leading-snug">Reimbursements Team</span>
            </label>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <span className="h-px flex-1 bg-slate-200" aria-hidden="true" />
          <span className="text-xs font-semibold text-slate-400">OR</span>
          <span className="h-px flex-1 bg-slate-200" aria-hidden="true" />
        </div>

        <label
          className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3.5 text-sm font-medium transition-all ${
            isSaveOnlyChecked
              ? 'border-[#1597ff] bg-sky-50/60 ring-1 ring-[#1597ff]/20 text-slate-900 shadow-2xs'
              : 'border-slate-200/90 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50/50'
          }`}
        >
          <input
            type="radio"
            name="public-notes-email-sendto"
            className="mt-0.5 h-4 w-4 shrink-0 border-slate-300 text-[#1597ff] focus:ring-blue-100 cursor-pointer"
            checked={isSaveOnlyChecked}
            onChange={selectSaveOnly}
            data-testid="public-notes-notify-just-save-radio"
          />
          <span className="min-w-0 flex-1 leading-snug">
            <span className="block font-semibold text-slate-900">Save note only</span>
            <span className="mt-0.5 block text-xs font-normal text-slate-500">
              No notification will be sent. Selecting this clears team choices.
            </span>
          </span>
        </label>
      </div>
    </ResidentDialogShell>
  )
}
